import { useState } from "react";
import { Alert, Button, Skeleton } from "@mantine/core";
import { IconArrowRight, IconCheck, IconChevronLeft, IconPlus } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { SmsGroupTemplateType, SmsTemplateStatus } from "@/models/club";
import smsTemplateService from "@/services/template";
import { SmsTemplate } from "@/services/template/type";
import { useGetSmsPlaceHolder } from "@/services/smsPlaceHolders/hooks/useGetSmsPlaceHolder";
import MessageText, { smsPartsOf } from "@/modules/marketing/MessageText";
import useAutoMessageStore from "@/store/autoMessage";
import classes from "./selectTemplate.module.css";

type Source = "suggested" | "personal";
const fa = (value: number) => value.toLocaleString("fa-IR");

// Step 1 of an automatic SMS: pick its text. Suggested texts for this message first (it is what most salons
// want), the salon's own approved templates one tap away; each option reads as the message the customer gets.
function SelectAutoMessageTemplate() {
  const { setTemplate, notifier, smsTemplate } = useAutoMessageStore();
  const navigate = useNavigate();
  const [source, setSource] = useState<Source>("suggested");
  const [selected, setSelected] = useState<SmsTemplate | undefined>(smsTemplate ?? notifier?.smsTemplate);
  const groupType = source === "personal" ? SmsGroupTemplateType.Value4 : notifier?.templateType;
  const query = useQuery({
    queryKey: ["allSmsTemplate", groupType],
    queryFn: () => smsTemplateService.getAllSmsTemplate(groupType!),
    enabled: groupType !== undefined,
  });
  const tags = useGetSmsPlaceHolder().data ?? [];

  if (!notifier) return <Navigate to="/dashboard/profile/autoMessage" replace />;

  const templates = (query.data ?? [])
    .flatMap((group) => group.templates ?? [])
    .filter((template) => template.status === SmsTemplateStatus.Value1);
  const next = () => {
    if (!selected?.id) return;
    setTemplate({ smsTemplate: selected });
    navigate("/dashboard/profile/autoMessage/selectTime");
  };

  return (
    <main className={classes.page} dir="rtl">
      <header className={classes.bar}>
        <Link to="/dashboard/profile/autoMessage" className={classes.back} aria-label="پیامک‌های خودکار">
          <IconArrowRight size={22} />
        </Link>
        <div className={classes.barTitle}>
          <strong>متن پیامک</strong>
          <span>{notifier.typeDisplay || "پیامک خودکار"}</span>
        </div>
        <span className={classes.step}>مرحله ۱ از ۲</span>
      </header>
      <div className={classes.progress}><span /></div>

      <section className={classes.body}>
        <div className={classes.intro}>
          <h1>چه متنی برای مشتری فرستاده شود؟</h1>
          <p>بخش‌های <span className={classes.legend}>بنفش</span> برای هر مشتری جداگانه پر می‌شوند.</p>
        </div>

        <div className={classes.tabs} role="tablist" aria-label="منبع متن">
          {([["suggested", "متن‌های پیشنهادی"], ["personal", "قالب‌های من"]] as const).map(([value, label]) => (
            <button key={value} type="button" role="tab" aria-selected={source === value} className={classes.tab} onClick={() => setSource(value)}>
              {label}
            </button>
          ))}
        </div>

        {query.isLoading ? (
          <div className={classes.list}>
            <Skeleton height={140} radius={16} />
            <Skeleton height={140} radius={16} />
          </div>
        ) : query.isError ? (
          <Alert color="red">
            دریافت متن‌ها انجام نشد.
            <Button variant="subtle" onClick={() => void query.refetch()}>تلاش دوباره</Button>
          </Alert>
        ) : templates.length ? (
          <div className={classes.list} role="radiogroup" aria-label="متن‌ها">
            {templates.map((template) => {
              const isSelected = selected?.id === template.id;
              const text = template.text ?? "";
              return (
                <button key={template.id} type="button" role="radio" aria-checked={isSelected} className={classes.option} onClick={() => setSelected(template)}>
                  <span className={classes.optionHead}>
                    <span className={classes.radio} aria-hidden="true">{isSelected && <IconCheck size={13} stroke={3.5} />}</span>
                    <strong>{template.title || "متن پیشنهادی"}</strong>
                    <span className={classes.parts}>{fa(smsPartsOf(text, tags))} پیامک</span>
                  </span>
                  <span className={classes.optionText}><MessageText text={text} tags={tags} /></span>
                </button>
              );
            })}
          </div>
        ) : (
          <div className={classes.empty}>
            {source === "personal" ? (
              <>
                <strong>هنوز قالب تأییدشده‌ای ندارید</strong>
                <span>متن پیشنهادی را انتخاب کنید یا قالب خودتان را بسازید.</span>
                <Button component={Link} to="/dashboard/profile/template/add" variant="light" color="violet" leftSection={<IconPlus size={16} />}>ساخت قالب</Button>
              </>
            ) : (
              <>
                <strong>برای این پیامک متن پیشنهادی نداریم</strong>
                <Button variant="light" color="violet" onClick={() => setSource("personal")}>دیدن قالب‌های من</Button>
              </>
            )}
          </div>
        )}
      </section>

      <footer className={classes.footer}>
        <div className={classes.footerInfo}>
          <span>{selected ? `متن: ${selected.title || "انتخاب‌شده"}` : "یک متن انتخاب کنید"}</span>
          {selected?.text && <strong>{fa(smsPartsOf(selected.text, tags))} پیامک برای هر نفر</strong>}
        </div>
        <Button size="md" color="violet" fullWidth disabled={!selected?.id} onClick={next} rightSection={<IconChevronLeft size={18} />}>
          ادامه؛ زمان ارسال
        </Button>
      </footer>
    </main>
  );
}

export default SelectAutoMessageTemplate;
